/**
 * 무대 위의 여럿이 판 - 전송 위의 매치 세션 + 사람 체인저 도구의 조립, 그리고 봇 판의 루프백 전송 만들기.
 *
 * 근거 문서: 로드맵 M2(봇과 한 판 - 루프백 전송) · M4(`stage.ts` - 바꾸기 진입에 도구를 세우고 수색 진입에 dispose)
 *            M6(`MatchStageRequest`에 전송 주입 자리 - 루프백/소켓을 무대가 구분하지 않는다)
 *
 * `stage.ts`에서 갈라져 나왔다(400줄 목표를 넘기지 않으려고 - 여럿이 몫이 M2·M4로 두 번 얹히며 그 파일이 넘었다). 하는 일은
 * 조립 둘이다 - (1) `createLoopbackMatch`: 호스트의 `deps`를 싱글과 같은 함수들로 채우고 봇 좌석을 앉혀 전송을 만든다(봇 판만),
 * (2) `buildMatchStage`: **어떤** 전송이든 그 위에 세션을 앉히고 도구를 세울 `stageChanger`를 세션의 문(`MatchChangerPort`)에 묶는다.
 * M6 이전에는 둘이 한 함수였다 - 소켓 방은 전송이 무대보다 먼저 생기므로(`welcome`을 받아야 내 좌석을 안다) 갈랐다.
 * 규칙은 없다. `ctx`는 인자로 받은 남의 것이다(`stage.ts` 머리말 - 이 파일도 `render/scene`을 가져오지 않는다. `app/stage.test.ts`가
 * "그 밖 전부"로 검사한다).
 */

import { CATALOG_BY_ID } from '../catalog/catalog'
import type { FloorPlan } from '../core/floorPlan'
import type { PlayerId, RoomConfig } from '../core/types'
import { generateChanges } from '../game/changeGen'
import { resolveShot } from '../game/hunt'
import { cpuDecide, type BotDifficulty } from '../game/match/cpu'
import { defaultMatchConfig, roomConfigFrom } from '../game/match/matchState'
import type { MatchConfig, MatchDeps } from '../game/match/matchTypes'
import type { MatchConfigView } from '../game/match/matchView'
import { createMatchSession } from '../game/matchSession'
import type { Tuning } from '../game/tuning'
import { t } from '../i18n'
import type { ChangerTool } from '../input/changerTool'
import type { DecorateTool } from '../input/decorateTool'
import type { IsoCamera } from '../input/isoCamera'
import { createBotMatch, type BotSeatSpec } from '../net/loopbackTransport'
import type { SceneCtx } from '../render/scene'
import type { MatchStageHandle, MatchStageRequest, StageRequest } from './stage'
import { createStageChanger } from './stageChanger'
import { createStageDecorate } from './stageDecorate'
import type { StageSession } from './stageSession'

/**
 * 봇 판의 내 좌석 id. 루프백에는 서버가 없어 신원을 발급할 곳이 없고, 좌석은 하나뿐이라 고정값으로 충분하다.
 * 봇 id는 `bot-1`부터다. 둘 다 콜론이 없다(`roomCodec.WIRE_PLACER`가 콜론으로 자기를 가른다).
 */
const LOCAL_MATCH_PLAYER_ID: PlayerId = 'me'
const BOT_ID_PREFIX = 'bot-'

export type MatchStageBuild = Readonly<{
  session: StageSession
  match: MatchStageHandle
  changer: () => ChangerTool | null
  decorator: () => DecorateTool | null
}>

/** 봇 판의 재료. 무대 요청(`StageRequest`)에서 호스트가 필요로 하는 것 + 봇 좌석의 모양. */
export type LoopbackMatchOptions = Readonly<{
  seed: string
  tuning: Tuning
  plan: FloorPlan
  targetObjectCount?: number | undefined
  /** 계정 탄창(`progress.ts`). 루프백 호스트가 내 좌석에 앉힌다 - 소켓 방은 0이다(서버가 계정 값을 검증할 길이 없다). */
  magazines?: number | undefined
  bots: number
  botLevel: BotDifficulty
}>

/**
 * 봇과 한 판의 전송(M2). 루프백 호스트(`createBotMatch`)를 같은 탭에 세우고 나와 봇들을 앉힌다 - 돌려주는 것은 무대 요청의
 * `match` 자리에 그대로 들어가는 모양이다. 방 시드는 `seed`를 그대로 쓴다 - `?seed=`로 봇 행동까지 재현되는 것이 M2의 완료
 * 판정이고, 시드가 둘이면 어느 것이 재현의 열쇠인지 갈린다.
 *
 * 호스트의 `deps`는 싱글 세션이 리듀서에 넘기는 것과 **같은 함수들**이다(`generateChanges` · `resolveShot`) - 봇 판의
 * 판정·변화가 싱글과 다른 함수를 지나면 패리티(`matchParity.test.ts`)가 화면에서 깨진다. 튜닝도 하나다(`deps.tuning` -
 * 호스트가 따로 받지 않는 이유는 `matchHost.ts` 머리말). 이름은 사전에서 온다 - 화면에 나가는 말이다.
 * **루프백 좌석 이름은 판을 세우는 시점의 언어로 굳는다.** 호스트 상태에 들어가 `ClientView.players[].name`으로 되돌아오므로
 * 판 도중 언어를 바꾸면 다른 오버레이는 `rebuildOverlays`로 새 언어인데 참가자 줄·순위표·"OO이 먼저 찾았다"의 이름만 옛
 * 언어로 남는다. 서버 판(M6)에서는 사람이 적은 이름이라 문제가 없고, 봇 판에서 판 도중 언어 전환은 설정 패널을 여는 일이라
 * 드물다 - id 규약(`bot-N`)을 화면 쪽 `nameOf`에 가르치는 비용보다 이 한 줄을 남기는 쪽을 택했다.
 * 좌석 순서가 곧 구획 회전의 방향이라(`matchZones.assignZones`) 사람이 먼저 앉는다(`createBotMatch` 주석).
 * 전송을 닫는 것은 세션의 `dispose`다.
 * 로비의 자동 진행(`ready` → `start`)은 여기도 세션도 아니라 `app/lobbyFlow.autopilot`이 한다 - 로비 없는 흐름이 호스트에 굳지 않게.
 */
export function createLoopbackMatch(options: LoopbackMatchOptions): MatchStageRequest {
  const deps: MatchDeps = {
    tuning: options.tuning,
    catalog: CATALOG_BY_ID,
    // 평면도는 좌석 수의 함수다(로드맵 M11). 루프백은 판을 세우는 시점에 좌석 수를 이미 알아(`main.startBotMatch`가
    // `partyPlanForSeats(bots + 1)`을 굽는다) 같은 평면도를 무대와 호스트에 함께 넘긴다 - 둘이 갈리면 소품이 벽을 뚫는다.
    planFor: () => options.plan,
    ...(options.targetObjectCount === undefined ? {} : { roomOptions: { targetObjectCount: options.targetObjectCount } }),
    generateChanges,
    resolveShot,
    cpuDecide,
  }
  const bots: BotSeatSpec[] = []
  // URL 경로(`urlState.resolveBotMatch`)는 이미 정수 1~7로 걸렀지만, 개발 훅 `__weirdRoom.startBotMatch(seed, bots)`는 그 검사를
  // 우회해 아무 수를 넘길 수 있다. 여기서 한 번 더 깎는 이유가 그것이다 - 상한은 화면의 것이라(참가자 줄) 여기서는 두지 않는다.
  for (let n = 1; n <= Math.max(0, Math.floor(options.bots)); n += 1) {
    bots.push({ id: `${BOT_ID_PREFIX}${n}`, name: t('party.botName', { n }), difficulty: options.botLevel })
  }
  // M11에서 역할이 사라져 첫 체인저를 고정할 자리가 없어졌다(전원이 자기 구획을 고친다) - 설정은 튜닝이 채우는 그대로다.
  const config: MatchConfig = defaultMatchConfig(options.tuning)
  const transport = createBotMatch({
    seed: options.seed,
    deps,
    me: { id: LOCAL_MATCH_PLAYER_ID, name: t('party.you'), magazines: options.magazines ?? 0 },
    bots,
    config,
  })
  return { transport, me: LOCAL_MATCH_PLAYER_ID, roomCode: transport.host.roomCode, host: transport.host }
}

/**
 * 뷰의 설정(`ClientView.config`) → 도구가 잠그는 방 규칙. 방장의 `configure`(M6)가 바꾼 값을 도구가 세워지는 순간(바꾸기 진입)에 읽는다 -
 * 루프백 기본값을 굳혀 두면 방장이 "옮김만"으로 바꾼 방에서 도구가 다섯 동사를 다 열어 두고, 호스트가 나머지 넷을 거절한다.
 * `placementsPerPlayer`는 뷰에 없다(꾸미기는 M10) - 튜닝 기본값 그대로다.
 */
function roomConfigOf(config: MatchConfigView | undefined, tuning: Tuning): RoomConfig {
  const base = roomConfigFrom(tuning)
  if (config === undefined) return base
  return { ...base, allowedVerbs: new Set(config.allowedVerbs), changeCount: config.changeCount }
}

/**
 * 여럿이 무대. **어떤** 전송이든 그 위에 매치 세션을 앉힌다 - 루프백(`createLoopbackMatch`)인지 소켓(`app/lobbyFlow.ts`)인지 여기는
 * 모른다. 세션은 `welcome`(또는 로비가 대신 청한 `resync`)이 오기 전까지 뷰가 없고, 그 사이 무대는 빈 방을 들고 서 있다.
 */
export function buildMatchStage(
  ctx: SceneCtx,
  iso: IsoCamera,
  plan: FloorPlan,
  request: StageRequest,
  match: MatchStageRequest,
): MatchStageBuild {
  const { transport, me } = match
  /*
   * 사람 체인저의 도구(M4). 세션은 도구를 조회 함수로만 알고(`changer`), 세우고 치우는 것은 무대다(`stageChanger.ts`) -
   * 바꾸기 진입에 서고 수색 진입에 사라진다. 세션이 먼저 만들어져야 하므로 `let`으로 늦게 잡는다.
   */
  let changer: ReturnType<typeof createStageChanger> | null = null
  /** 꾸미기 도구(M10). 체인저와 같은 규약 - 세션이 먼저 서야 하므로 늦게 잡는다. */
  let decorator: ReturnType<typeof createStageDecorate> | null = null
  const session = createMatchSession({
    ctx,
    iso,
    bounds: plan.bounds,
    catalog: CATALOG_BY_ID,
    plan,
    tuning: request.tuning,
    transport,
    ...(match.retainTransport === undefined ? {} : { retainTransport: match.retainTransport }),
    me,
    seed: request.seed,
    mount: document.body,
    startActive: request.startActive,
    onMagazineUsed: request.onMagazineUsed,
    onSound: request.onSound,
    // 결과 화면의 [나가기]. 방을 떠나는 것은 앱의 화면 전환(`main.showSelect`)이라 세션은 콜백만 받는다.
    onExitToSelect: request.onExitToSelect,
    onPhaseEnter: (phase) => {
      changer?.onPhaseEnter(phase)
      decorator?.onPhaseEnter(phase)
    },
    changer: () => changer?.current() ?? null,
    decorator: () => decorator?.current() ?? null,
  })
  changer = createStageChanger({
    ctx,
    catalog: CATALOG_BY_ID,
    bounds: plan.bounds,
    // 도구가 잠그는 허용 동사·개수 = 호스트가 뷰에 실어 준 지금 설정(방장의 `configure` 반영). 세워지는 순간 읽는다.
    config: () => roomConfigOf(session.getView()?.config, request.tuning),
    seed: request.seed,
    me,
    mount: document.body,
    port: session.changer,
    // 계정의 값이라 무대가 읽지 않고 위에서 내려온다(`StageRequest`). 생략된 판(싱글·배경)에서는 안내가 서지 않는다.
    needsCoach: request.needsCoach ?? (() => false),
    onCoached: request.onCoached ?? (() => undefined),
  })
  decorator = createStageDecorate({
    ctx,
    catalog: CATALOG_BY_ID,
    bounds: plan.bounds,
    seed: request.seed,
    me,
    mount: document.body,
    port: session.decorator,
  })
  const built = changer
  const builtDecorator = decorator
  return {
    session,
    match: {
      view: session.getView,
      host: match.host,
      transport,
      act: session.act,
      dispose: () => {
        built.dispose()
        builtDecorator.dispose()
      },
    },
    changer: () => built.current(),
    decorator: () => builtDecorator.current(),
  }
}
